import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSandboxArtifacts, useSandboxChanges } from '../../views/hooks/useOntologyViews';
import { useSandboxSession } from '../SandboxSessionContext';
import { parseDraftReviewRoute, viewBranchPreviewPath } from '../utils/previewDestination';

/** One review owner. All detail renderers receive this resolved revision. */
export function useBranchReview(options = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const inOverlay = Object.prototype.hasOwnProperty.call(options, 'branchId');
  const branchId = inOverlay ? options.branchId : parseDraftReviewRoute(location.pathname).sandboxId;
  const requestedApprovalId = inOverlay ? options.approvalId : params.get('approval') || null;
  const requestedRevision = inOverlay ? options.revisionId : params.get('revision') || params.get('overlay') || null;
  const { sandboxes = [], selectPreviewSandbox, isLoadingSandboxes, isFetchingSandboxes, sandboxesError } = useSandboxSession();
  const currentBranch = sandboxes.find((branch) => branch.sandbox_id === branchId);
  // The eligible queue owns the decision, including Automation continuations.
  // A branch-only link must not create an independent publication request.
  const approvalId = requestedApprovalId || currentBranch?.publication?.approval_id || null;
  const revisionId = currentBranch?.working_review_id && currentBranch.working_review_id === approvalId
    ? currentBranch.active_overlay_id : requestedRevision;
  const eligible = Boolean(currentBranch
    && (!revisionId || currentBranch.active_overlay_id === revisionId)
    && (!approvalId || (currentBranch.preview_approval_ids || [currentBranch.publication?.approval_id]).includes(approvalId)));
  const loadingEligibility = !currentBranch && (isLoadingSandboxes || isFetchingSandboxes);
  const rawQuery = useSandboxChanges({
    sandboxId: branchId,
    approvalId,
    overlayId: revisionId,
    preferCommitted: true,
    enabled: Boolean(branchId && eligible),
  });
  // Do not render a cached historical review after its authority is gone.
  const eligibilityError = branchId && !eligible && !loadingEligibility
    ? sandboxesError || new Error('This draft is not awaiting your review here. It may already be applied, declined or withdrawn, or belong to another chat.') : null;
  const query = eligible ? rawQuery : { ...rawQuery, data: undefined,
    isLoading: Boolean(loadingEligibility), isError: Boolean(eligibilityError), error: eligibilityError };
  const review = query.data?.review;
  const resolvedRevision = review?.revision_id || null;
  // Resolve once, then make the pin shareable. A later approval cannot replace
  // this review's content or authorize an unpublished working revision.
  useEffect(() => {
    if (inOverlay || !branchId || !resolvedRevision || revisionId) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('revision', resolvedRevision);
        return next;
      },
      { replace: true },
    );
  }, [inOverlay, branchId, resolvedRevision, revisionId, setParams]);
  const artifactsQuery = useSandboxArtifacts({
    sandboxId: branchId,
    overlayId: resolvedRevision,
    enabled: Boolean(resolvedRevision && !query.isError),
  });
  const latestRevision = currentBranch?.active_overlay_id;
  const context = useMemo(
    () => ({
      branchId,
      revisionId: resolvedRevision,
      approvalId,
      contentHash: review?.content_hash,
      sandboxContext: review?.sandbox_context,
    }),
    [branchId, resolvedRevision, approvalId, review],
  );
  const openLatest = () =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('approval');
      next.delete('artifact');
      if (latestRevision) next.set('revision', latestRevision);
      else next.delete('revision');
      return next;
    });
  const openBranchPreview = (viewId) => {
    if (!branchId || !resolvedRevision || !viewId) return;
    selectPreviewSandbox(
      branchId,
      review?.sandbox_context || { sandbox_id: branchId, active_overlay_id: resolvedRevision },
    );
    navigate(viewBranchPreviewPath({ id: viewId }, { sandboxId: branchId, revisionId: resolvedRevision }));
  };
  return {
    branchId,
    approvalId,
    query,
    review,
    context,
    isPinningRevision: !inOverlay && Boolean(resolvedRevision && !revisionId),
    changes: query.data,
    branch: query.data?.sandbox,
    canArchive: Boolean(
      currentBranch?.can_discard && !['published', 'archived', 'discarded'].includes(query.data?.sandbox?.sandbox_status),
    ),
    artifactsQuery,
    artifacts: artifactsQuery.data?.artifacts || [],
    hasNewerRevision: Boolean(resolvedRevision && latestRevision && latestRevision !== resolvedRevision),
    latestRevision,
    openLatest,
    openBranchPreview,
    selection: { artifactId: params.get('artifact'), family: params.get('family'), name: params.get('name') },
  };
}
